import { useEffect } from 'react';

/**
 * Hook to manage hreflang and canonical tags for regional SEO pages
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.currentUrl - The canonical URL of the current page
 * @param {Object} config.alternates - Object with regional URLs { de: '...', at: '...', ch: '...' }
 * @param {string} [config.xDefault] - Optional x-default URL (usually the generic overview page)
 */
export function useHreflang({ currentUrl, alternates, xDefault = null }) {
  useEffect(() => {
    // Remove any existing hreflang tags (cleanup)
    const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflangs.forEach(tag => tag.remove());

    // Set canonical (self-referential)
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;

    // Create hreflang tags
    const hreflangTags = [];

    // de-DE (Germany)
    if (alternates.de) {
      const tagDE = document.createElement('link');
      tagDE.rel = 'alternate';
      tagDE.hreflang = 'de-DE';
      tagDE.href = alternates.de;
      document.head.appendChild(tagDE);
      hreflangTags.push(tagDE);
    }

    // de-AT (Austria)
    if (alternates.at) {
      const tagAT = document.createElement('link');
      tagAT.rel = 'alternate';
      tagAT.hreflang = 'de-AT';
      tagAT.href = alternates.at;
      document.head.appendChild(tagAT);
      hreflangTags.push(tagAT);
    }

    // de-CH (Switzerland)
    if (alternates.ch) {
      const tagCH = document.createElement('link');
      tagCH.rel = 'alternate';
      tagCH.hreflang = 'de-CH';
      tagCH.href = alternates.ch;
      document.head.appendChild(tagCH);
      hreflangTags.push(tagCH);
    }

    // x-default (generic fallback, optional)
    if (xDefault) {
      const tagDefault = document.createElement('link');
      tagDefault.rel = 'alternate';
      tagDefault.hreflang = 'x-default';
      tagDefault.href = xDefault;
      document.head.appendChild(tagDefault);
      hreflangTags.push(tagDefault);
    }

    // Cleanup on unmount
    return () => {
      hreflangTags.forEach(tag => tag.remove());
    };
  }, [currentUrl, alternates, xDefault]);
}

// Pre-defined URL sets for regional pages
export const HREFLANG_SETS = {
  buchhaltung: {
    de: 'https://ivoliq.io/buchhaltung-freelancer-deutschland',
    at: 'https://ivoliq.io/buchhaltung-freelancer-oesterreich',
    ch: 'https://ivoliq.io/buchhaltung-freelancer-schweiz',
    xDefault: 'https://ivoliq.io/freelancer-buchhaltung',
  },
  steuerruecklagen: {
    de: 'https://ivoliq.io/steuerruecklagen-freelancer-deutschland',
    at: 'https://ivoliq.io/steuerruecklagen-freelancer-oesterreich',
    ch: 'https://ivoliq.io/steuerruecklagen-freelancer-schweiz',
    xDefault: 'https://ivoliq.io/steuerruecklagen-freelancer',
  },
  rechnungsvorlage: {
    de: 'https://ivoliq.io/rechnungsvorlage-freelancer-deutschland',
    at: 'https://ivoliq.io/rechnungsvorlage-freelancer-oesterreich',
    ch: 'https://ivoliq.io/rechnungsvorlage-freelancer-schweiz',
    xDefault: 'https://ivoliq.io/rechnungsvorlage-freelancer',
  },
};

export default useHreflang;
